import { useEffect, useMemo, useState } from 'react'
import { api, type InventoryItemNameRow, type StorefrontCategory } from '@/lib/api'
import { filterCategoriesWithInventory } from '@/lib/categoryInventoryFilter'
import { itemAllowsCart } from '@/lib/inventoryItemDisplay'

// One request per page load, shared by the header picker, category chips and home feed.
let categoriesRequest: Promise<StorefrontCategory[]> | null = null

function loadCategories(): Promise<StorefrontCategory[]> {
  if (!categoriesRequest) {
    categoriesRequest = api.storefront
      .getCategories()
      .then((res) => (res.success && res.data ? res.data : []))
      .catch(() => {
        categoriesRequest = null
        return []
      })
  }
  return categoriesRequest
}

/**
 * Storefront categories in display order. `visibleCategories` keeps only those with at least one
 * in-stock item, since out-of-stock items are hidden from browsing.
 */
export function useStorefrontCategories(
  inventoryItems: InventoryItemNameRow[],
  inventoryLoading = false
) {
  const [categories, setCategories] = useState<StorefrontCategory[]>([])

  useEffect(() => {
    let cancelled = false
    loadCategories().then((rows) => {
      if (!cancelled) setCategories(rows)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const visibleCategories = useMemo(() => {
    if (inventoryLoading) return []
    const sorted = [...categories].sort(
      (a, b) => (a.display_order ?? Number.MAX_SAFE_INTEGER) - (b.display_order ?? Number.MAX_SAFE_INTEGER)
    )
    return filterCategoriesWithInventory(sorted, inventoryItems.filter((r) => itemAllowsCart(r.in_stock)))
  }, [categories, inventoryItems, inventoryLoading])

  return { categories, visibleCategories }
}
